// @ts-nocheck
import { useState, useMemo, useEffect } from "react";
import type { ReactNode } from "react";
import {
  Button, Modal, Form, Input, InputNumber, Select, Switch, Space,
  message, Dropdown,
} from "../components/admin-ui";
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ApiOutlined, MessageOutlined, SendOutlined,
  MoreOutlined, CheckCircleOutlined, CloseCircleOutlined,
} from "../components/admin-icons";
import { useTranslation } from "react-i18next";
import type { ColumnsType } from "../components/admin-ui";
import type { SmsProvider } from "@kawkaw/shared";
import { DataTable } from "../components/DataTable";
import { tokens } from "../styles/tokens";
import { AdminShell } from "../layouts/AdminShell";
import {
  useSmsProviders, useCreateSmsProvider,
  useUpdateSmsProvider, useDeleteSmsProvider, useTestSmsProvider, useSubmitSmsTemplate,
} from "../api/hooks";

const { color: T } = tokens;

function SmsStatCard({ label, value, icon, iconColor, badge, badgeColor, badgeBg }: {
  label: string;
  value: number;
  icon: ReactNode;
  iconColor: string;
  badge: string;
  badgeColor: string;
  badgeBg: string;
}) {
  return (
    <div className="admin-kpi-card admin-sms-provider-stat">
      <div className="admin-kpi-heading flex">
        <div className="admin-kpi-label">{label}</div>
        <span className="admin-kpi-icon" style={{ color: iconColor, background: badgeBg }}>{icon}</span>
      </div>
      <div className="admin-kpi-value">{value.toLocaleString()}</div>
      <div className="admin-provider-stat-note" style={{ color: badgeColor }}>{badge}</div>
    </div>
  );
}

const PROVIDER_ICONS: Record<string, string> = {
  twilio: "📱", sms123: "💬", custom: "⚙️",
};

// Twilio's actual send URL is built server-side from the Account SID
// (TwilioMessagesClient), so base_url is unused for it — left blank.
// sms123.net's real API (per its public docs) is form-encoded POST with
// apiKey/recipients/messageContent as request params, not JSON — the
// template below matches that exactly so a freshly-created sms123 row
// works out of the box, no manual config editing needed.
// Verified 2026-08-25 against sms123.net's own published docs
// (https://www.sms123.net/apiIntegration.php) — it's a GET endpoint reading
// params from the URL query string only, NOT a POST body of any kind
// (the original "form" POST default was wrong and silently sent every
// field to a server that never read it — that read back as "invalid API
// key" even with a correct one, since the key never actually arrived).
// referenceID is a required param, not optional.
const PROVIDER_DEFAULTS: Record<string, { base_url: string; config: Record<string, unknown> }> = {
  sms123: {
    base_url: "https://www.sms123.net/api/send.php",
    config: {
      content_type: "query",
      method: "GET",
      auth_style: "none",
      body_template: { apiKey: "{{apiKey}}", recipients: "{{phone}}", messageContent: "{{message}}", referenceID: "{{referenceId}}" },
      success_check: { field: "status", equals: "ok" },
      error_code_field: "msgCode",
      permanent_error_codes: ["E00242"],
      transient_error_codes: ["E00250", "E00359"],
    },
  },
};

// A body-field's value is picked from this fixed set of placeholders — an
// admin never types a raw {{...}} token, they just pick "Phone Number" etc.
// from a dropdown. "Fixed text" is the only case that needs typing, for
// provider-specific constants (e.g. a sender ID) the API requires verbatim.
const VALUE_TYPES = ["phone", "message", "apiKey", "apiSecret", "referenceId", "custom"] as const;
const PLACEHOLDER_FOR_TYPE: Record<(typeof VALUE_TYPES)[number], string> = {
  phone: "{{phone}}", message: "{{message}}", apiKey: "{{apiKey}}", apiSecret: "{{apiSecret}}", referenceId: "{{referenceId}}", custom: "",
};
const TYPE_FOR_PLACEHOLDER: Record<string, (typeof VALUE_TYPES)[number]> = {
  "{{phone}}": "phone", "{{message}}": "message", "{{apiKey}}": "apiKey", "{{apiSecret}}": "apiSecret", "{{referenceId}}": "referenceId",
};

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}`;

const DEFAULT_BODY_FIELDS = () => [
  { id: nextRowId(), key: "to", type: "phone" as const, custom: "" },
  { id: nextRowId(), key: "message", type: "message" as const, custom: "" },
];

/** Builds the config_json object from the friendly-builder state. */
function buildConfigFromFields(fields: {
  bodyFields: { id: string; key: string; type: string; custom: string }[];
  contentType: string;
  authStyle: string;
  successField: string;
  successValue: string;
  errorCodeField: string;
  permanentCodes: string;
  transientCodes: string;
}): Record<string, unknown> {
  const body_template: Record<string, string> = {};
  for (const f of fields.bodyFields) {
    if (!f.key.trim()) continue;
    body_template[f.key.trim()] = f.type === "custom" ? f.custom : PLACEHOLDER_FOR_TYPE[f.type as (typeof VALUE_TYPES)[number]] ?? "";
  }
  const config: Record<string, unknown> = {
    content_type: fields.contentType,
    auth_style: fields.authStyle,
    body_template,
  };
  if (fields.successField.trim()) config.success_check = { field: fields.successField.trim(), equals: fields.successValue };
  if (fields.errorCodeField.trim()) config.error_code_field = fields.errorCodeField.trim();
  const perm = fields.permanentCodes.split(",").map((s) => s.trim()).filter(Boolean);
  if (perm.length) config.permanent_error_codes = perm;
  const trans = fields.transientCodes.split(",").map((s) => s.trim()).filter(Boolean);
  if (trans.length) config.transient_error_codes = trans;
  return config;
}

/** Best-effort reverse of buildConfigFromFields, for editing an existing row. Never throws. */
function parseConfigIntoFields(raw: unknown): {
  bodyFields: { id: string; key: string; type: string; custom: string }[];
  contentType: string;
  authStyle: string;
  successField: string;
  successValue: string;
  errorCodeField: string;
  permanentCodes: string;
  transientCodes: string;
} {
  const fallback = {
    bodyFields: DEFAULT_BODY_FIELDS(), contentType: "form", authStyle: "none",
    successField: "", successValue: "", errorCodeField: "", permanentCodes: "", transientCodes: "",
  };
  try {
    const obj = typeof raw === "string" ? (raw.trim() ? JSON.parse(raw) : {}) : (raw as Record<string, unknown> | null) ?? {};
    const bodyTemplate = (obj.body_template as Record<string, unknown>) ?? {};
    const bodyFields = Object.entries(bodyTemplate).map(([key, value]) => {
      const str = String(value);
      const type = TYPE_FOR_PLACEHOLDER[str] ?? "custom";
      return { id: nextRowId(), key, type, custom: type === "custom" ? str : "" };
    });
    const successCheck = obj.success_check as { field?: string; equals?: unknown } | undefined;
    return {
      bodyFields: bodyFields.length ? bodyFields : DEFAULT_BODY_FIELDS(),
      contentType: (obj.content_type as string) ?? "form",
      authStyle: (obj.auth_style as string) ?? "none",
      successField: successCheck?.field ?? "",
      successValue: successCheck?.equals != null ? String(successCheck.equals) : "",
      errorCodeField: (obj.error_code_field as string) ?? "",
      permanentCodes: Array.isArray(obj.permanent_error_codes) ? obj.permanent_error_codes.join(", ") : "",
      transientCodes: Array.isArray(obj.transient_error_codes) ? obj.transient_error_codes.join(", ") : "",
    };
  } catch {
    return fallback;
  }
}

export function SmsProvidersScreen() {
  const { t } = useTranslation();
  const providerOptions = useMemo(
    () =>
      (
        [
          ["twilio", "providerTwilio"],
          ["sms123", "providerSms123"],
          ["custom", "providerCustom"],
        ] as const
      ).map(([value, key]) => ({ value, label: t(`smsProviders.${key}`) })),
    [t],
  );
  const providerLabel = (v: string) => providerOptions.find(p => p.value === v)?.label ?? v;
  const valueTypeOptions = useMemo(
    () => VALUE_TYPES.map((v) => ({ value: v, label: t(`smsProviders.value_${v}`) })),
    [t],
  );

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<SmsProvider | null>(null);
  const [testModalOpen, setTestModalOpen] = useState(false);
  const [testTarget, setTestTarget] = useState<SmsProvider | null>(null);
  const [testPhone, setTestPhone] = useState("");
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [templateTarget, setTemplateTarget] = useState<SmsProvider | null>(null);
  const [templateTitle, setTemplateTitle] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [form] = Form.useForm();

  // Friendly config builder state — only relevant when provider === "custom".
  // Twilio/sms123 never touch this at all (fields hidden, defaults baked in
  // server-side by PROVIDER_DEFAULTS), so a non-technical admin only ever
  // sees raw JSON if they explicitly opt into "Advanced" mode.
  const [selectedProvider, setSelectedProvider] = useState<string | undefined>(undefined);
  const [rawMode, setRawMode] = useState(false);
  const [bodyFields, setBodyFields] = useState(DEFAULT_BODY_FIELDS());
  const [contentType, setContentType] = useState("form");
  const [authStyle, setAuthStyle] = useState("none");
  const [successField, setSuccessField] = useState("");
  const [successValue, setSuccessValue] = useState("");
  const [errorCodeField, setErrorCodeField] = useState("");
  const [permanentCodes, setPermanentCodes] = useState("");
  const [transientCodes, setTransientCodes] = useState("");

  // Keep the (possibly hidden) config_json form field in sync with the
  // friendly builder, so submit/raw-mode-toggle always see the latest shape.
  useEffect(() => {
    if (selectedProvider !== "custom" || rawMode) return;
    const config = buildConfigFromFields({ bodyFields, contentType, authStyle, successField, successValue, errorCodeField, permanentCodes, transientCodes });
    form.setFieldValue("config_json", JSON.stringify(config, null, 2));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProvider, rawMode, bodyFields, contentType, authStyle, successField, successValue, errorCodeField, permanentCodes, transientCodes]);

  const resetCustomBuilder = () => {
    setBodyFields(DEFAULT_BODY_FIELDS());
    setContentType("form");
    setAuthStyle("none");
    setSuccessField("");
    setSuccessValue("");
    setErrorCodeField("");
    setPermanentCodes("");
    setTransientCodes("");
  };

  // Auto-fill base_url + config_json from the known template whenever the
  // provider changes — an admin picking "sms123" shouldn't have to hand-type
  // (or mistype) its API host and body shape. Picking "Custom API" resets
  // the friendly builder to a blank starting template instead of a stale
  // prior value.
  const applyProviderDefaults = (provider: string) => {
    setSelectedProvider(provider);
    setRawMode(false);
    // Hiding a credential field (see render below) doesn't clear whatever
    // was typed into it — without this, switching e.g. Twilio -> sms123
    // would silently keep submitting a stale api_secret/webhook_secret the
    // admin can no longer even see. webhook_secret is repurposed per
    // provider (Twilio SID / sms123 email), not cleared between those two;
    // only cleared when switching to "custom", which uses neither.
    if (provider === "sms123") form.setFieldsValue({ api_secret: undefined });
    else if (provider === "custom") form.setFieldsValue({ webhook_secret: undefined });

    const known = PROVIDER_DEFAULTS[provider];
    if (known) {
      form.setFieldsValue({ base_url: known.base_url, config_json: JSON.stringify(known.config, null, 2) });
    } else {
      form.setFieldsValue({ base_url: undefined, config_json: undefined });
      if (provider === "custom") resetCustomBuilder();
    }
  };

  const toggleRawMode = () => {
    if (!rawMode) {
      setRawMode(true);
      return;
    }
    // Switching back to the friendly builder — re-parse whatever the admin
    // typed in raw mode so the two views stay honest with each other.
    const parsed = parseConfigIntoFields(form.getFieldValue("config_json"));
    setBodyFields(parsed.bodyFields);
    setContentType(parsed.contentType);
    setAuthStyle(parsed.authStyle);
    setSuccessField(parsed.successField);
    setSuccessValue(parsed.successValue);
    setErrorCodeField(parsed.errorCodeField);
    setPermanentCodes(parsed.permanentCodes);
    setTransientCodes(parsed.transientCodes);
    setRawMode(false);
  };

  const addBodyField = () => setBodyFields((rows) => [...rows, { id: nextRowId(), key: "", type: "custom", custom: "" }]);
  const removeBodyField = (id: string) => setBodyFields((rows) => rows.filter((r) => r.id !== id));
  const updateBodyField = (id: string, patch: Partial<{ key: string; type: string; custom: string }>) =>
    setBodyFields((rows) => rows.map((r) => (r.id === id ? { ...r, ...patch } : r)));

  const { data, isLoading } = useSmsProviders();
  const createProvider = useCreateSmsProvider();
  const updateProvider = useUpdateSmsProvider();
  const deleteProvider = useDeleteSmsProvider();
  const testProvider = useTestSmsProvider();
  const submitTemplate = useSubmitSmsTemplate();

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    setSelectedProvider(undefined);
    setRawMode(false);
    resetCustomBuilder();
    setModalOpen(true);
  };
  const openEdit = (row: SmsProvider) => {
    setEditing(row);
    form.setFieldsValue({
      ...row,
      config_json: row.config_json ? JSON.stringify(row.config_json, null, 2) : undefined,
    });
    setSelectedProvider(row.provider);
    setRawMode(false);
    if (row.provider === "custom") {
      const parsed = parseConfigIntoFields(row.config_json);
      setBodyFields(parsed.bodyFields);
      setContentType(parsed.contentType);
      setAuthStyle(parsed.authStyle);
      setSuccessField(parsed.successField);
      setSuccessValue(parsed.successValue);
      setErrorCodeField(parsed.errorCodeField);
      setPermanentCodes(parsed.permanentCodes);
      setTransientCodes(parsed.transientCodes);
    }
    setModalOpen(true);
  };

  const onSave = async (values: Record<string, unknown>) => {
    try {
      const body = { ...values } as Record<string, unknown>;
      if (typeof body.config_json === "string") {
        body.config_json = body.config_json.trim() ? JSON.parse(body.config_json as string) : null;
      }
      if (editing) {
        await updateProvider.mutateAsync({ id: editing.id, ...body });
        void message.success(t("smsProviders.updateSuccess"));
      } else {
        await createProvider.mutateAsync(body);
        void message.success(t("smsProviders.createSuccess"));
      }
      setModalOpen(false);
    } catch { void message.error(t("smsProviders.saveFailed")); }
  };

  const onDelete = async (id: string) => {
    try { await deleteProvider.mutateAsync(id); void message.success(t("smsProviders.deleteSuccess")); }
    catch { void message.error(t("smsProviders.deleteFailed")); }
  };

  const confirmDelete = (row: SmsProvider) => {
    Modal.confirm({
      title: t("smsProviders.confirmDelete"),
      okText: t("common.yes"),
      cancelText: t("common.no"),
      danger: true,
      onOk: () => onDelete(row.id),
    });
  };

  const openTest = (row: SmsProvider) => { setTestTarget(row); setTestPhone(""); setTestModalOpen(true); };

  const onTest = async () => {
    if (!testTarget) return;
    try {
      const res = await testProvider.mutateAsync({ id: testTarget.id, ...(testPhone.trim() ? { phone: testPhone } : {}) });
      const result = (res as { data?: { success: boolean; message: string; latency_ms?: number } }).data;
      if (result?.success) {
        void message.success(
          result.latency_ms != null
            ? t("common.connectionTestOkWithLatency", { message: result.message, ms: result.latency_ms })
            : t("common.connectionTestOk", { message: result.message }),
        );
      } else {
        void message.error(
          t("common.connectionTestFailed", {
            message: result?.message ?? t("transactions.testFailedGeneric"),
          }),
        );
      }
      setTestModalOpen(false);
    } catch { void message.error(t("smsProviders.testFailed")); }
  };

  const openTemplateSubmit = (row: SmsProvider) => {
    setTemplateTarget(row);
    setTemplateTitle("");
    setTemplateContent("");
    setTemplateModalOpen(true);
  };

  const onSubmitTemplate = async () => {
    if (!templateTarget || !templateTitle.trim() || !templateContent.trim()) return;
    try {
      const res = await submitTemplate.mutateAsync({ id: templateTarget.id, templateTitle: templateTitle.trim(), messageContent: templateContent.trim() });
      const result = (res as { data?: { success: boolean; message: string } }).data;
      if (result?.success) void message.success(result.message);
      else void message.error(result?.message ?? t("smsProviders.templateSubmitFailed"));
      setTemplateModalOpen(false);
    } catch { void message.error(t("smsProviders.templateSubmitFailed")); }
  };

  const items = (data as { data?: SmsProvider[] } | undefined)?.data ?? [];
  const activeCount = items.filter(item => item.is_active).length;
  const inactiveCount = items.length - activeCount;
  const healthyCount = items.filter(item => item.is_healthy).length;

  const columns: ColumnsType<SmsProvider> = [
    {
      title: t("smsProviders.colProvider"), dataIndex: "provider", key: "provider", width: 140,
      render: (v: string) => (
        <span className="admin-data-badge admin-sms-provider-badge">
          {PROVIDER_ICONS[v] ?? "📦"} {providerLabel(v)}
        </span>
      ),
    },
    {
      title: t("smsProviders.colDisplayName"), dataIndex: "display_name", key: "display_name",
      render: (v: string) => <span className="admin-data-customer">{v}</span>,
    },
    {
      title: t("smsProviders.colSortOrder"), dataIndex: "sort_order", key: "sort_order", width: 100,
      render: (v: number) => <span className="admin-data-amount">{v}</span>,
    },
    {
      title: t("smsProviders.colHealthy"), dataIndex: "is_healthy", key: "is_healthy", width: 110,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.successText : T.dangerText, background: v ? T.successBg : T.dangerBg }}>
          {v ? t("smsProviders.healthy") : t("smsProviders.unhealthy")}
        </span>
      ),
    },
    {
      title: t("smsProviders.colStatus"), dataIndex: "is_active", key: "active", width: 90,
      render: (v: boolean) => (
        <span className="admin-data-badge" style={{ color: v ? T.successText : T.textSecondary, background: v ? T.successBg : T.surfaceSection }}>
          {v ? t("common.yes") : t("common.no")}
        </span>
      ),
    },
    {
      title: t("smsProviders.colActions"), key: "actions", width: 96, fixed: "right" as const,
      render: (_: unknown, row: SmsProvider) => (
        <Dropdown
          menu={{ items: [
            { key: "test", icon: <ApiOutlined />, label: t("smsProviders.testConnection"), onClick: () => openTest(row) },
            ...(row.provider === "sms123" ? [{ key: "template", icon: <SendOutlined />, label: t("smsProviders.submitTemplateTooltip"), onClick: () => openTemplateSubmit(row) }] : []),
            { key: "edit", icon: <EditOutlined />, label: t("common.edit"), onClick: () => openEdit(row) },
            { key: "delete", icon: <DeleteOutlined />, label: t("common.delete"), danger: true, onClick: () => confirmDelete(row) },
          ] }}
          trigger={["click"]}
          placement="bottomRight"
        >
          <Button className="admin-sms-provider-more admin-data-more" type="text" loading={testProvider.isPending}
            aria-label={`${t("smsProviders.colActions")} ${row.display_name}`} icon={<MoreOutlined style={{ fontSize: 18 }} />} />
        </Dropdown>
      ),
    },
  ];

  const isCustom = selectedProvider === "custom";
  const isKnownProvider = !!selectedProvider && !isCustom;

  return (
    <AdminShell>
      <div className="admin-sms-providers-page admin-list-page">
        <div className="admin-provider-page-inner">
          <div className="admin-provider-toolbar">
            <div>
              <h1 className="admin-page-title">{t("smsProviders.title")}</h1>
              <div className="admin-provider-subtitle">{t("smsProviders.subtitle")}</div>
            </div>
            <Button className="admin-provider-create" type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              {t("smsProviders.addProvider")}
            </Button>
          </div>

          <section className="admin-sms-providers-stats admin-txn-stats grid">
            <SmsStatCard label={t("smsProviders.statTotal")} value={items.length} icon={<MessageOutlined />} iconColor={T.brand} badge={t("smsProviders.statBadgeOverall")} badgeColor={T.brand} badgeBg={T.brandLight} />
            <SmsStatCard label={t("smsProviders.statActive")} value={activeCount} icon={<CheckCircleOutlined />} iconColor={T.successText} badge={t("smsProviders.statBadgeActive")} badgeColor={T.successText} badgeBg={T.successBg} />
            <SmsStatCard label={t("smsProviders.statHealthy")} value={healthyCount} icon={<ApiOutlined />} iconColor={T.infoText} badge={t("smsProviders.statBadgeHealthy")} badgeColor={T.infoText} badgeBg={T.infoBg} />
            <SmsStatCard label={t("smsProviders.statInactive")} value={inactiveCount} icon={<CloseCircleOutlined />} iconColor={T.textSecondary} badge={t("smsProviders.statBadgeInactive")} badgeColor={T.textSecondary} badgeBg={T.surfaceSection} />
          </section>

          <div className="admin-provider-results">
            {t("common.total", { count: items.length })}
          </div>

          <div className="admin-sms-providers-table-panel admin-data-table-panel">
            <DataTable<SmsProvider>
              columns={columns}
              dataSource={items}
              rowKey="id"
              loading={isLoading}
              pagination={{ pageSize: 10 }}
            />
          </div>
        </div>
      </div>

      <Modal
        open={modalOpen}
        title={
          <span className="admin-provider-dialog-title">
            <MessageOutlined />
            {editing ? t("smsProviders.editTitle") : t("smsProviders.newTitle")}
          </span>
        }
        onCancel={() => setModalOpen(false)}
        onOk={() => void form.submit()}
        okText={t("common.save")}
        okButtonProps={{ style: { background: T.brandAmber, borderColor: T.brandAmber }, loading: createProvider.isPending || updateProvider.isPending }}
        width={640}
        maskClosable={false}
      >
        <Form className="admin-sms-provider-form" form={form} layout="vertical" colon={false} onFinish={onSave}>
          <div className="admin-provider-section-label">{t("smsProviders.sectionGeneral")}</div>
          <div className="admin-provider-form-fields">
            <Form.Item name="provider" label={t("smsProviders.formProvider")} rules={[{ required: true }]}>
              <Select options={providerOptions} placeholder={t("smsProviders.phSelectProvider")} onChange={applyProviderDefaults} />
            </Form.Item>
            <Form.Item name="display_name" label={t("smsProviders.formDisplayName")} rules={[{ required: true }]}>
              <Input placeholder={t("smsProviders.phDisplayName")} />
            </Form.Item>
          </div>
          <div className="admin-provider-section-label">{t("smsProviders.sectionCredentials")}</div>
          <Form.Item
            name="api_key"
            label={selectedProvider === "twilio" ? t("smsProviders.formAccountSid") : t("smsProviders.formApiKey")}
          >
            <Input.Password
              placeholder={selectedProvider === "twilio" ? t("smsProviders.formAccountSidPlaceholder") : t("smsProviders.formApiKeyPlaceholder")}
              visibilityToggle
            />
          </Form.Item>
          {/* sms123.net only has a single API key — no secret, no messaging-service concept — showing
              these unused fields for it was actively misleading, not just unnecessary. */}
          {selectedProvider !== "sms123" && (
            <Form.Item
              name="api_secret"
              label={selectedProvider === "twilio" ? t("smsProviders.formAuthToken") : t("smsProviders.formApiSecret")}
            >
              <Input.Password
                placeholder={selectedProvider === "twilio" ? t("smsProviders.formAuthTokenPlaceholder") : t("smsProviders.formApiSecretPlaceholder")}
                visibilityToggle
              />
            </Form.Item>
          )}
          {/* Reused per-provider like api_key/api_secret: Twilio -> Messaging Service SID
              (buildSmsClient), sms123 -> sms123.net account email (required by their
              smsAddTemplate.php for template-approval submission). Unused/hidden for custom
              since GenericHttpSmsClient never reads this column at all. */}
          {selectedProvider === "twilio" && (
            <Form.Item name="webhook_secret" label={t("smsProviders.formMessagingServiceSid")} extra={t("smsProviders.formMessagingServiceSidHint")}>
              <Input.Password placeholder={t("smsProviders.formMessagingServiceSidPlaceholder")} visibilityToggle />
            </Form.Item>
          )}
          {selectedProvider === "sms123" && (
            <Form.Item name="webhook_secret" label={t("smsProviders.formSms123Email")} extra={t("smsProviders.formSms123EmailHint")}>
              <Input placeholder={t("smsProviders.formSms123EmailPlaceholder")} />
            </Form.Item>
          )}

          {isKnownProvider && (
            <div className="admin-sms-provider-note">
              <ApiOutlined /> {t("smsProviders.builtInNote", { provider: providerLabel(selectedProvider!) })}
            </div>
          )}

          {isCustom && (
            <>
              <Form.Item name="base_url" label={t("smsProviders.formBaseUrl")} rules={[{ required: true }]}>
                <Input placeholder={t("smsProviders.phBaseUrl")} />
              </Form.Item>

              {!rawMode ? (
                <div className="admin-sms-custom-config">
                  <div style={{ fontSize: 13, fontWeight: 900, color: T.textDark, marginBottom: 4 }}>{t("smsProviders.customConfigTitle")}</div>
                  <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 14 }}>{t("smsProviders.customConfigHint")}</div>

                  <Space wrap style={{ width: "100%" }} size={12}>
                    <Form.Item label={t("smsProviders.contentTypeLabel")} style={{ flex: 1 }}>
                      <Select
                        value={contentType}
                        onChange={setContentType}
                        options={[
                          { value: "query", label: t("smsProviders.contentTypeQuery") },
                          { value: "form", label: t("smsProviders.contentTypeForm") },
                          { value: "json", label: t("smsProviders.contentTypeJson") },
                        ]}
                      />
                    </Form.Item>
                    <Form.Item label={t("smsProviders.authStyleLabel")} style={{ flex: 1 }}>
                      <Select
                        value={authStyle}
                        onChange={setAuthStyle}
                        options={[
                          { value: "none", label: t("smsProviders.authStyleNone") },
                          { value: "bearer", label: t("smsProviders.authStyleBearer") },
                        ]}
                      />
                    </Form.Item>
                  </Space>

                  <div style={{ fontSize: 12, fontWeight: 800, color: T.textDark, marginTop: 4, marginBottom: 8 }}>{t("smsProviders.bodyFieldsLabel")}</div>
                  {bodyFields.map((f) => (
                    <div key={f.id} style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 8, alignItems: "center" }}>
                      <Input
                        value={f.key}
                        onChange={(e) => updateBodyField(f.id, { key: e.target.value })}
                        placeholder={t("smsProviders.fieldNamePlaceholder")}
                        style={{ flex: 1, minWidth: 120 }}
                      />
                      <Select
                        value={f.type}
                        onChange={(type: string) => updateBodyField(f.id, { type })}
                        options={valueTypeOptions}
                        style={{ flex: 1, minWidth: 120 }}
                      />
                      {f.type === "custom" && (
                        <Input
                          value={f.custom}
                          onChange={(e) => updateBodyField(f.id, { custom: e.target.value })}
                          placeholder={t("smsProviders.customValuePlaceholder")}
                          style={{ flex: 1, minWidth: 120 }}
                        />
                      )}
                      <Button size="small" danger icon={<DeleteOutlined />} onClick={() => removeBodyField(f.id)} />
                    </div>
                  ))}
                  <Button size="small" icon={<PlusOutlined />} onClick={addBodyField} style={{ marginBottom: 16 }}>
                    {t("smsProviders.addField")}
                  </Button>

                  <div style={{ fontSize: 12, fontWeight: 800, color: T.textDark, marginBottom: 4 }}>{t("smsProviders.advancedErrorHandling")}</div>
                  <div style={{ fontSize: 11, color: T.textMuted, marginBottom: 10 }}>{t("smsProviders.advancedErrorHandlingHint")}</div>
                  <Space wrap style={{ width: "100%" }} size={8}>
                    <Form.Item label={t("smsProviders.successFieldLabel")} style={{ flex: 1 }}>
                      <Input value={successField} onChange={(e) => setSuccessField(e.target.value)} placeholder={t("smsProviders.successFieldPlaceholder")} />
                    </Form.Item>
                    <Form.Item label={t("smsProviders.successValueLabel")} style={{ flex: 1 }}>
                      <Input value={successValue} onChange={(e) => setSuccessValue(e.target.value)} placeholder={t("smsProviders.successValuePlaceholder")} />
                    </Form.Item>
                  </Space>
                  <Form.Item label={t("smsProviders.errorCodeFieldLabel")}>
                    <Input value={errorCodeField} onChange={(e) => setErrorCodeField(e.target.value)} placeholder={t("smsProviders.errorCodeFieldPlaceholder")} />
                  </Form.Item>
                  <Form.Item label={t("smsProviders.permanentCodesLabel")} extra={t("smsProviders.permanentCodesHint")}>
                    <Input value={permanentCodes} onChange={(e) => setPermanentCodes(e.target.value)} placeholder={t("smsProviders.codesPlaceholder")} />
                  </Form.Item>
                  <Form.Item label={t("smsProviders.transientCodesLabel")} extra={t("smsProviders.transientCodesHint")}>
                    <Input value={transientCodes} onChange={(e) => setTransientCodes(e.target.value)} placeholder={t("smsProviders.codesPlaceholder")} />
                  </Form.Item>

                  <Button size="small" onClick={toggleRawMode}>{t("smsProviders.editRawJson")}</Button>
                </div>
              ) : (
                <>
                  <Form.Item name="config_json" label={t("smsProviders.formConfigJson")} extra={t("smsProviders.configJsonHint")}>
                    <Input.TextArea rows={6} placeholder={t("smsProviders.phConfigJson")} />
                  </Form.Item>
                  <Button size="small" onClick={toggleRawMode} style={{ marginBottom: 14 }}>{t("smsProviders.backToSimpleForm")}</Button>
                </>
              )}
            </>
          )}

          <div className="admin-provider-section-label">{t("smsProviders.sectionOperations")}</div>
          <div className="admin-provider-form-fields admin-provider-form-fields--operations">
            <Form.Item name="sort_order" label={t("smsProviders.formSortOrder")} initialValue={0}
              extra={t("smsProviders.sortOrderHint")}>
              <InputNumber min={0} style={{ width: "100%" }} />
            </Form.Item>
            <Form.Item className="admin-provider-switch-card" name="is_active" label={t("smsProviders.formIsActive")} valuePropName="checked" initialValue={true}>
              <Switch checkedChildren={t("common.yes")} unCheckedChildren={t("common.no")} />
            </Form.Item>
          </div>
        </Form>
      </Modal>

      <Modal
        open={testModalOpen}
        title={<span className="admin-provider-dialog-title"><ApiOutlined />{t("smsProviders.testConnection")}</span>}
        onCancel={() => setTestModalOpen(false)}
        onOk={() => void onTest()}
        okText={t("smsProviders.sendTest")}
        okButtonProps={{ loading: testProvider.isPending }}
        maskClosable={false}
      >
        <Form className="admin-provider-compact-form" layout="vertical" colon={false}>
          <Form.Item label={t("smsProviders.testPhoneLabel")} extra={t("smsProviders.testPhoneHint")}>
            <Input value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder={t("smsProviders.testPhonePlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        open={templateModalOpen}
        title={<span className="admin-provider-dialog-title"><SendOutlined />{t("smsProviders.submitTemplateTitle")}</span>}
        onCancel={() => setTemplateModalOpen(false)}
        onOk={() => void onSubmitTemplate()}
        okText={t("smsProviders.submitTemplateOk")}
        okButtonProps={{ loading: submitTemplate.isPending, disabled: !templateTitle.trim() || !templateContent.trim() }}
        maskClosable={false}
      >
        <div className="admin-provider-modal-note">
          {t("smsProviders.submitTemplateHint")}
        </div>
        <Form className="admin-provider-compact-form" layout="vertical" colon={false}>
          <Form.Item label={t("smsProviders.templateTitleLabel")}>
            <Input value={templateTitle} onChange={(e) => setTemplateTitle(e.target.value)} placeholder={t("smsProviders.templateTitlePlaceholder")} />
          </Form.Item>
          <Form.Item label={t("smsProviders.templateContentLabel")} extra={t("smsProviders.templateContentHint")}>
            <Input.TextArea value={templateContent} onChange={(e) => setTemplateContent(e.target.value)} rows={3} maxLength={500} placeholder={t("smsProviders.templateContentPlaceholder")} />
          </Form.Item>
        </Form>
      </Modal>
    </AdminShell>
  );
}
